import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import usePageMeta from '@/hooks/usePageMeta';
import { categories, comics } from '@/mocks/comics';

const sortOptions = [
  { key: 'hot', label: '人气最高' },
  { key: 'rating', label: '评分最高' },
  { key: 'new', label: '最新更新' },
];

export default function Catalog() {
  usePageMeta({
    title: '漫画大全',
    description: '漫画免费全集与全本资源聚合列表，支持按分类筛选与关键词搜索。热血冒险、恋爱少女、玄幻奇幻等分类快速找书，追更补番更省心。',
    keywords: '漫画免费,漫画大全,漫画免费全集,漫画免费目录,漫画分类',
  });
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get('c') || '全部';
  const searchQ = params.get('q') || '';
  const [sort, setSort] = useState('hot');
  const [localSearch, setLocalSearch] = useState(searchQ);

  const setCategory = (cat: string) => {
    const next = new URLSearchParams(params);
    if (cat === '全部') next.delete('c');
    else next.set('c', cat);
    setParams(next, { replace: true });
  };

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = localSearch.trim();
    const next = new URLSearchParams(params);
    if (kw) next.set('q', kw);
    else next.delete('q');
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = comics;
    if (activeCategory !== '全部') {
      list = list.filter((c) => c.category === activeCategory);
    }
    if (searchQ) {
      const q = searchQ.toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.includes(searchQ)),
      );
    }
    const sorted = [...list];
    if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === 'new') sorted.sort((a, b) => b.year - a.year);
    else sorted.sort((a, b) => Number(b.views.replace('亿', '').replace('万', '')) - Number(a.views.replace('亿', '').replace('万', '')));
    return sorted;
  }, [activeCategory, searchQ, sort]);

  return (
    <main>
      <section className="bg-background-100 py-10 md:py-14">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="mb-6">
            <nav className="mb-2 text-sm text-foreground-500">
              <span>首页</span> <i className="ri-arrow-right-s-line" /> <span>漫画大全</span>
            </nav>
            <h1 className="font-heading text-3xl text-foreground-950">漫画大全</h1>
            <p className="mt-2 text-sm text-foreground-600">漫画免费全集与全本资源聚合列表，按分类快速找书，追更补番更省心。</p>
          </div>

          <form onSubmit={applySearch} className="mb-4">
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5">
                <i className="ri-search-line text-foreground-500" />
                <input
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  placeholder="搜索漫画名称、作者或标签"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </div>
          </form>

          <div className="mb-4 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 py-3">
            <p className="text-sm text-foreground-500">
              共 <span className="font-medium text-foreground-800">{filtered.length}</span> 部作品
              {searchQ && <>，关键词「<span className="text-primary-600">{searchQ}</span>」</>}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {sortOptions.map((o) => (
                <button
                  key={o.key}
                  onClick={() => setSort(o.key)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                    sort === o.key ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 md:gap-5">
              {filtered.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-background-200 bg-background-100 py-20 text-center">
              <i className="ri-inbox-line text-5xl text-foreground-300" />
              <p className="mt-4 text-foreground-600">没有找到匹配的漫画，换个关键词试试</p>
              <button
                onClick={() => {
                  setLocalSearch('');
                  setParams(new URLSearchParams(), { replace: true });
                }}
                className="mt-4 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                清空筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}